import { IAvatar } from './avatar';
import { IBrandingType, IMap, IMapLayoutCatalog } from './map';
import { IParticipant } from './participants';
import { IRoom } from './rooms';
import { IStreamService } from './streamService';
import { IMillicastEventTokens } from './millicastTokens';
import { WsConnectionClientType } from './index';
import { IEventParticipant } from './eventParticipant';
import { IMytaverseFile } from './file';
import { IWorld } from './worlds';
import { StreamingProviders } from '@core/dashboard/providers/MytaverseEventProvider/interfaces';
import { PollyLanguage } from '@shared/interfaces/polly';

import {
  ExperienceFeatures,
  type IBaseFeatureConfig,
  type IExperienceFeatureSettings,
} from '@shared/interfaces/events';

export enum EventStatus {
  SERVER_INITIALIZATION = 'SERVER_INITIALIZATION',
  TESTING = 'TESTING',
  SERVER_SCALE_DOWN = 'SERVER_SCALE_DOWN',
  SCHEDULED = 'SCHEDULED',
  SERVER_SCALE_UP = 'SERVER_SCALE_UP',
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  FINISHED = 'FINISHED',
  SUSPENDED = 'SUSPENDED',
  DELETED = 'DELETED',
  ERROR = 'ERROR',
  FAILED = 'FAILED',
  CANCELED = 'CANCELED',
}

export interface IIdleTimerSettings {
  timeout?: number; // seconds
  counterTimeout?: number; // seconds
  enabled?: boolean;
}

export interface IEventAssistantVoiceByLanguage {
  engine: string;
  voiceId: string;
}

export interface IEventSettingsAiAssistant {
  engine: string;
  voiceId: string;
  language: PollyLanguage;
  baseLanguage?: PollyLanguage;
  languages?: PollyLanguage[];
  voicesByLanguage?: Partial<
    Record<PollyLanguage, IEventAssistantVoiceByLanguage>
  >;
}

export { ExperienceFeatures };
export type { IBaseFeatureConfig, IExperienceFeatureSettings };

export interface IMapFeatureFlagConfig {
  enabled: boolean;
  config?: string;
}

// Per-(mapId, slug) overrides. Slugs without an entry default to enabled.
export interface IMapFeatureFlagsSettings {
  [mapId: string]: {
    [slug: string]: IMapFeatureFlagConfig;
  };
}

export interface IEventSettings {
  //AI
  /** @deprecated Use `features[ExperienceFeatures.AiAssistant].enabled` instead. */
  aiEnabled?: boolean; //enableAssistant
  aiVirtualAssistantName?: string; //todo: apply where necessary
  aiAssistant?: IEventSettingsAiAssistant;
  /** @deprecated Use `features[ExperienceFeatures.AskMeAnything].enabled` instead. */
  aiAMAEnabled?: boolean; //"Ask Me Anything" free-form Q&A — enabled by default when undefined

  //Timers
  timerIdleSettings?: IIdleTimerSettings; //idleTimer
  timerLoadingTimeMins?: number;

  //General
  generalDisabledKeys?: string; //todo: apply where necessary

  //Server
  serverSuspendAfterPublish?: boolean; //suspendAfterPublish

  //Features
  features?: IExperienceFeatureSettings;

  //Map feature flags
  mapFeatureFlags?: IMapFeatureFlagsSettings;
}

export interface IEvent {
  eventId: string;
  customUrl: string;
  endTime: number;
  image: string;
  name: string;
  startTime: number;
  streamServiceId: string;
  user: string;
  eventReleaseId?: string;
  logoImage: IMytaverseFile;
  agendaImage: string;
  participants: IParticipant[];
  eventAvatars: IAvatar[];
  rooms: IRoom[];
  maps: IMap[];
  clientChangeset?: string;
  avatars: IAvatar[];
  streamService?: IStreamService;
  dolbyConferenceId: string;
  region?: string;
  lastJoinTime?: number;
  eventRelease: IEventRelease;
  role: string;
  loadingScreenAsset?: IMytaverseFile | string | null;
  backgroundImage?: IMytaverseFile;
  worldId: string;
  world?: IWorld | null;
  status: EventStatus;
  eventTags?: string[];
  inviteSlotsTags?: string[];
  //enableAssistant?: boolean; //deprecated
  //enablePureGamecast?: string; //deprecated
  gamecastPixelStreamingClientId?: string;
  gamecastPixelStreamingClient?: IPixelStreamingClient | null;
  spsApplication?: {
    spsApplicationId: string;
    wsUrl: string;
  };

  externalUrl?: string;
  eventLocation?: string;
  isDisabled?: boolean;
  disabledKeyCommands?: string[];
  isFirstTimeTooltip?: boolean;

  inviteStartTime?: number;
  isScheduled?: boolean;
  isAlwaysOn?: boolean;

  //idleTimer?: IIdleTimerSettings; //deprecate
  //loadingTimeMins?: number; //deprecate

  settings?: IEventSettings;
}

export interface IEventRelease {
  eventReleaseId: string;
  name: string;
  timestamp: number;
  status: EventReleaseStatus;
  eventId: string;
  region: string;
  gameliftEC2InstanceRegion: string;
  stateMachineExecutionArn: string;
  gameliftFleetId?: string;
  gamecastPixelStreamingClientId?: string;
  comment?: string;
  serverChangeset?: string;
  clientChangeset?: string;
  version?: string;
  clientVersion?: string;
  serverVersion?: string;
  gamecastLocations?: string[];
  streamClasses: string[];
  pixelStreamingClientIds?: string[];
  pixelStreamingClients?: IPixelStreamingClient[];
  gameliftServersBuildId?: string;
}

export enum EventReleaseStatus {
  INITIALIZATION = 'INITIALIZATION',
  ACTIVE = 'ACTIVE',
  FAILED = 'FAILED',
  DELETED = 'DELETED',
}

export enum PlayerSessionStatus {
  ACTIVE = 'ACTIVE',
  COMPLETED = 'COMPLETED',
  RESERVED = 'RESERVED',
  TIMEDOUT = 'TIMEDOUT',
}

export interface PlayerSession {
  PlayerSessionId?: string;
  PlayerId?: string;
  GameSessionId?: string;
  FleetId?: string;
  FleetArn?: string;
  CreationTime?: Date;
  TerminationTime?: Date;
  Status?: PlayerSessionStatus;
  IpAddress?: string;
  DnsName?: string;
  Port?: number;
  PlayerData?: string;
}

export interface IEventTokens {
  dolbyToken: string;
  streamChatToken: string;
  millicastTokens: IMillicastEventTokens;
  gamelift: {
    playerSession: PlayerSession;
  };
}

export enum FollowingStatus {
  Accepted = 'ACCEPTED',
  Pending = 'PENDING',
}

export interface IFollower {
  following: string; // adminId
  eventId: string;
  participantId: string;
  clientType: WsConnectionClientType;
  followingStatus: FollowingStatus;
}

export interface IFollowingDataResponse {
  followers: IFollower[];
  groupLeads: string[];
}

export interface IParticipantEvents {
  events: IEventParticipant[];
  lastKey?: IEventParticipant | null;
}

export enum PixelStreamingBuildType {
  Development = 'DEVELOPMENT',
  Shipping = 'SHIPPING',
}

export enum PixelStreamingClientReleaseType {
  Release = 'RELEASE',
  Prerelease = 'PRERELEASE',
}

export enum PixelStreamingClientReleaseStatus {
  Releasing = 'RELEASING',
  Released = 'RELEASED',
  Failed = 'FAILED',
  Canceled = 'CANCELED',
}

/**
 * One level as the build's `metadata.json` describes it, under
 * `clientParams.resources.maps`. Only the fields the hub reads are spelled out.
 */
export interface IPixelStreamingClientResourceMap {
  /** Asset path of the level, e.g. `/Game/Maps/Map_SpiralMuseum`. */
  name?: string;
  /**
   * Marks a level no participant can ever be in: a constructor prefab room, a
   * sublevel, a test level. UE starts sending it with MV-1826; until then it is
   * absent, and `null`/`false` mean the same thing — a content map. Read it
   * strictly as `=== true`.
   */
  isServiceMap?: boolean | null;
  regions?: unknown[];
  spots?: unknown[];
  /**
   * Per-map override for the assistant's "Teach me how to move" entry point.
   * Absent on builds that don't author it — treat as "no override" and fall
   * back to the client-side default in that case, not as `false`.
   */
  guidedTutorial?: boolean;
}

/**
 * Wire types a spawnable-object property may declare. `enum` carries its
 * choices in the entry's `options`; `vector`, `rotator` and `color` go over
 * the wire as number arrays; `file` is the URL of an uploaded file the engine
 * fetches, `''` when none is set.
 */
export type SpawnablePropertyType =
  | 'string'
  | 'bool'
  | 'int'
  | 'float'
  | 'enum'
  | 'vector'
  | 'rotator'
  | 'color'
  | 'object'
  | 'file';

/**
 * One editable property of a runtime-spawnable type. `displayName` is ready to
 * use as a form label; `name` is the key sent back in a spawn/update payload's
 * `properties` object.
 */
export interface ISpawnableObjectProperty {
  name: string;
  displayName: string;
  type: SpawnablePropertyType;
  /** enum only: the values the property accepts. */
  options?: string[];
}

/**
 * One runtime-spawnable engine type from the build's
 * `clientParams.spawnableObjects` schema. `typeId` is the stable wire id (sent
 * as `type` in spawn payloads).
 *
 * `class` is optional because the real exporter omits the key entirely — the
 * ticket's example carried it, a captured `metadata.json` does not. It is
 * informational only and never sent, so nothing reads it.
 */
export interface ISpawnableObject {
  typeId: string;
  displayName: string;
  class?: string;
  properties: ISpawnableObjectProperty[];
}

export interface IPixelStreamingClientParams {
  containerTag?: string;
  version?: string;
  streamGroupId?: string;
  applicationId?: string;
  region?: string;
  additionalLaunchArgs?: string;
  buildUri?: string;
  description?: string;
  streamClass?: string;
  executablePath?: string;
  logLocations?: string[];
  runtimeEnvironment?: {
    type: string;
    version: string;
  };
  resources?: {
    maps: IPixelStreamingClientResourceMap[];
  };
  // The map-editor room kits this build ships, keyed by the ueIdentifier of the map each one
  // hosts — the same last-path-segment derivation the map records themselves are keyed by.
  //
  // Written onto the build's own record by the upload (spatial-manager's syncBuildLayoutCatalog),
  // which is the only writer of a kit now. `IMap.layoutCatalog` is the slot that predates that
  // move: it was one entry shared by every build naming the map, last-writer-wins, so an unrelated
  // upload could leave it describing geometry the streamed build does not have. Nothing writes it
  // any more, and since MV-2004 nothing reads it either — this is the only kit the hub resolves.
  layoutCatalogs?: Record<string, IMapLayoutCatalog>;
  // Runtime-spawnable types this build exports (spatial-manager's
  // syncBuildSpawnableObjects writes it onto the build's own record). Absent
  // means UNKNOWN — an older build without the exporter — never "no spawnable
  // types": consumers hide the spawn UI rather than render an empty picker.
  spawnableObjects?: ISpawnableObject[];

  // Schemas of the brandable classes this build ships, one entry per class,
  // sorted by `entityType`. Written onto the build's own record by the upload
  // from `plugins.branding.types`.
  //
  // Lives on the build rather than on a map for the same reason `layoutCatalogs`
  // does: it describes what the streamed build knows about, and a map can be
  // served by several builds. Absent means the build predates MV-1905; an empty
  // array means it declares no brandable class.
  brandingTypes?: IBrandingType[];
  // The module renders this build shipped, published out of its package by the upload and keyed
  // by catalog element id -> absolute URL (MV-1953).
  //
  // Absolute, and resolved on the way in rather than joined here. The catalog's own
  // `thumbnailUrl` is relative to wherever the build's `WebEditor/` is hosted, and for a release
  // nothing hosted it: the hub put the relative path into an `<img src>`, the browser resolved it
  // against the current SPA route, and the SPA fallback answered with HTTP 200 and a page of
  // HTML — a "successful" request whose decode failed, which is why it read as a build that had
  // shipped no renders.
  //
  // Absent or short is normal: most of a shipped kit has no cached render, and a build can be
  // packaged without the export pass entirely. Those elements fall back to the drawing.
  layoutThumbnails?: Record<string, string>;

  // The full-size module renders, same shape and same publish as `layoutThumbnails` — the tile is
  // this very capture box-filtered to 320x180, so the two never disagree about what a module looks
  // like.
  //
  // Carried apart because the surfaces differ: the tree and its small tiles want the tile, while
  // the picker's chosen segment and the segment inspector fill a panel-wide frame a 320x180 tile
  // can only be upscaled into.
  //
  // Absent is the common case rather than a defect — the render is opt-in and off by default in
  // every pipeline, so an ordinary build ships tiles and no previews at all while its catalog still
  // names a `previewUrl` for every element.
  layoutPreviews?: Record<string, string>;
}

export interface IPixelStreamingClient {
  clientId: string;
  eventId?: string;
  clientType: StreamingProviders;
  buildType?: PixelStreamingBuildType;
  version?: string;
  clientChangeset: string;
  name?: string;
  timestamp: number;
  clientParams: IPixelStreamingClientParams;
  coreweaveClientData?: {
    spsApplicationId?: string;
    spsVersionId?: string;
    spsWSEndpoint?: string;
  };
  gamecastClientData?: IGamecastClientData;
  tags?: string[];
  releaseType?: PixelStreamingClientReleaseType;
  releaseStatus?: PixelStreamingClientReleaseStatus;
  status?: string;
  deletedAt?: number;
}

export interface IGamecastClientData {
  streamGroupId?: string | null;
  applicationId?: string;
  region?: string;
  additionalLaunchArgs?: string[];

  buildUri?: string;
  description?: string;
  streamClass?: string;
  tags?: string[];
  clientToken?: string;
}

export interface IGetStreamingProviderResponse {
  streamingProvider: StreamingProviders | null;
  pixelStreamingClient: IPixelStreamingClient;
}

export interface IParticipantAvatar {
  isAvatarTermsAccepted?: boolean | null;
  customAvatarUrl?: string;
  avatarId?: string;
}
